import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Button,
  Skeleton,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  BrokenImage as BrokenImageIcon,
  SportsSoccer as SportsSoccerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import accountService from '../services/accountService';
import secureLocalStorage from 'react-secure-storage';
import { getTemplateStyle } from '../theme/templateStyles';
import placeholderImage from '../../images/No-Image-Placeholder.png';
import { isProductOutOfStock } from '../utils/stock';

const ProductCard = ({ product, onAddToCart, isInWishlist: initialIsInWishlist }) => {
  const navigate = useNavigate();
  const templateName = useSelector((state) => state.template?.template);
  const tplStyle = getTemplateStyle(templateName);
  // 'standard' (current default) | 'centered' | 'chips' | 'quickAdd'
  const cardInfo = tplStyle.cardInfo || 'standard';
  const isCentered = cardInfo === 'centered';
  // Per-template image aspect ratio; null keeps the original responsive ratio
  const imageAspect = tplStyle.cardAspect || { xs: '140%', sm: '135%', md: '133%' };
  const [isFavorite, setIsFavorite] = useState(initialIsInWishlist || false);
  const [imageHover, setImageHover] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(false);
  const [, setIsLoggedIn] = useState(false);
  const [, setCustomerId] = useState(null);

  // Check if user is logged in and load wishlist status
  useEffect(() => {
    // If isInWishlist prop is provided, use it directly (e.g., from Wishlist page)
    if (initialIsInWishlist !== undefined) {
      setIsFavorite(initialIsInWishlist);
      return;
    }

    const storedCustomerId = secureLocalStorage.getItem('customerId');
    if (storedCustomerId) {
      setIsLoggedIn(true);
      setCustomerId(storedCustomerId);
      // For authenticated users, don't check API here
      // Only check when user opens the product detail page
      setIsFavorite(false); // Default to false
    } else {
      // Only check localStorage wishlist for non-authenticated users
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        try {
          const wishlistItems = JSON.parse(savedWishlist);
          const isInWishlist = wishlistItems.some(item => (item.id || item._id) === (product.id || product._id));
          setIsFavorite(isInWishlist);
        } catch (e) {
          localStorage.removeItem('wishlist');
        }
      }
    }
  }, [product, initialIsInWishlist]);

  const handleProductClick = () => {
    navigate(`/products/${product.id}`);
  };

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    setIsLoadingWishlist(true);

    try {
      const result = await accountService.toggleWishlistItem(product);

      if (result.success) {
        setIsFavorite(result.inWishlist);
        setSnackbarMessage(result.message || (result.inWishlist ? 'Added to Wishlist ❤️' : 'Removed from Wishlist'));
      } else {
        setSnackbarMessage(result.error || 'Failed to update wishlist');
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      setSnackbarMessage('Error updating wishlist');
      setSnackbarOpen(true);
    } finally {
      setIsLoadingWishlist(false);
    }
  };

  // Callers pass either a numeric price (mapped products) or a preformatted
  // "KSH 1,234" string (Home's section mapping) — normalize to one label.
  const hasNumericPrices = typeof product.price === 'number' && typeof product.originalPrice === 'number';
  const priceLabel = typeof product.price === 'number'
    ? `KSH ${product.price.toLocaleString()}`
    : (product.price || 'KSH 0');
  const productImage = product.imageUrl || product.image || '';

  const discountPercentage = hasNumericPrices && product.originalPrice > 0
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  // Prefer a precomputed flag (Products page drops pricing), else derive from pricing.
  const outOfStock = product.outOfStock ?? isProductOutOfStock(product);

  return (
    <Box
      onClick={handleProductClick}
      onMouseEnter={() => setImageHover(true)}
      onMouseLeave={() => setImageHover(false)}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all var(--motion-speed, 0.3s) var(--motion-ease, ease)',
        borderRadius: 'var(--card-radius, 0px)',
        '&:hover': {
          transform: 'translateY(var(--card-hover-lift, -2px))',
          boxShadow: 'var(--card-shadow, 0 4px 12px rgba(0,0,0,0.1))',
        }
      }}
    >
      {/* Image Container - Ultra Compact */}
      <Box
        sx={{
          position: 'relative',
          paddingTop: imageAspect,
          backgroundColor: tplStyle.imageFit === 'contain' ? '#ffffff' : '#f8f8f8',
          overflow: 'hidden',
          borderRadius: 'var(--card-radius, 0px)',
          mb: 0,
        }}
      >
        {/* Sale Badge - Minimal */}
        {discountPercentage > 0 && !outOfStock && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 6, sm: 8, md: 10 },
              left: { xs: 6, sm: 8, md: 10 },
              backgroundColor: '#ff4444',
              color: '#ffffff',
              px: { xs: 0.75, sm: 1 },
              py: { xs: 0.25, sm: 0.4 },
              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              letterSpacing: '0.02em',
              zIndex: 2,
              borderRadius: '4px',
            }}
          >
            -{discountPercentage}%
          </Box>
        )}

        {/* Out of Stock Badge - shown when every variation is out of stock */}
        {outOfStock && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 6, sm: 8, md: 10 },
              left: { xs: 6, sm: 8, md: 10 },
              backgroundColor: 'rgba(0,0,0,0.75)',
              color: '#ffffff',
              px: { xs: 0.75, sm: 1 },
              py: { xs: 0.25, sm: 0.4 },
              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              zIndex: 3,
              borderRadius: '4px',
            }}
          >
            Out of Stock
          </Box>
        )}

        {/* Favorite Button - Mobile Optimized */}
        <IconButton
          onClick={handleToggleFavorite}
          disabled={isLoadingWishlist}
          size="small"
          sx={{
            position: 'absolute',
            top: { xs: 8, sm: 10, md: 12 },
            right: { xs: 8, sm: 10, md: 12 },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 10,
            width: { xs: 32, sm: 36, md: 36 },
            height: { xs: 32, sm: 36, md: 36 },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'white',
              transform: 'scale(1.1)',
              boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
            },
            '&:disabled': {
              opacity: 0.7,
            },
          }}
        >
          {isLoadingWishlist ? (
            <CircularProgress
              size={18}
              sx={{
                color: '#ff4444',
              }}
            />
          ) : isFavorite ? (
            <FavoriteIcon
              sx={{
                fontSize: { xs: 14, sm: 16, md: 18 },
                color: '#ff4444',
                animation: isFavorite ? 'pulse 0.3s ease' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.2)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            />
          ) : (
            <FavoriteBorderIcon
              sx={{
                fontSize: { xs: 14, sm: 16, md: 18 },
                color: '#999',
                transition: 'all 0.2s ease',
              }}
            />
          )}
        </IconButton>

        {/* Loading Skeleton */}
        {!imageLoaded && !imageError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Skeleton
              variant="rectangular"
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: 'grey.200',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <SportsSoccerIcon
                sx={{
                  fontSize: 40,
                  color: 'text.disabled',
                  opacity: 0.4,
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ opacity: 0.6, mt: 0.5 }}>
                Loading product...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Error State */}
        {imageError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <BrokenImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              Image unavailable
            </Typography>
          </Box>
        )}

        {/* Product Image with Progressive Loading */}
        <Fade in={imageLoaded} timeout={600}>
          <Box
            component="img"
            src={productImage || placeholderImage}
            alt={product.name}
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'var(--image-fit, cover)',
              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: imageHover ? 'scale(var(--image-zoom, 1.08))' : 'scale(1)',
              opacity: imageLoaded ? 1 : 0,
            }}
          />
        </Fade>

        {/* View Details Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            p: 1.5,
            opacity: imageHover ? 1 : 0,
            transform: imageHover ? 'translateY(0)' : 'translateY(100%)',
            transition: 'all 0.3s ease',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            zIndex: 10,
            display: cardInfo === 'quickAdd' ? 'none' : { xs: 'none', sm: 'block' },
          }}
        >
          <Button
            fullWidth
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              handleProductClick();
            }}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              fontWeight: 500,
              py: 0.75,
              px: 1.5,
              borderRadius: '6px',
              color: '#333',
              border: '1px solid #e0e0e0',
              backgroundColor: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'var(--primary)',
                color: 'white',
                borderColor: 'var(--primary)',
              },
            }}
          >
            View Details
          </Button>
        </Box>
      </Box>

      {/* Product Info - layout varies with the store template */}
      <Box
        className="product-info"
        onClick={handleProductClick}
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 2.5, md: 3 },
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0.75, sm: 1, md: 1.25 },
          cursor: 'pointer',
          ...(isCentered && { alignItems: 'center', textAlign: 'center' }),
        }}
      >
        {cardInfo === 'chips' ? (
          product.category && (
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Typography
                sx={{
                  display: 'inline-block',
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                  fontWeight: 600,
                  color: 'var(--primary)',
                  border: '1px solid color-mix(in srgb, var(--primary) 40%, transparent)',
                  borderRadius: '999px',
                  px: 1,
                  py: 0.25,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  lineHeight: 1.4,
                }}
              >
                {product.category}
              </Typography>
            </Box>
          )
        ) : (
          <Typography
            sx={{
              fontSize: { xs: '0.625rem', sm: '0.6875rem', md: '0.75rem' },
              fontWeight: 400,
              color: '#999',
              letterSpacing: isCentered ? '0.12em' : '0.02em',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {product.category}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.9rem' },
            fontWeight: 500,
            color: '#000',
            lineHeight: { xs: 1.2, sm: 1.3 },
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: { xs: '2.4em', sm: '2.6em' },
          }}
        >
          {product.name}
        </Typography>

        {/* Price */}
        <Box sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: { xs: 0.5, sm: 0.8 },
          mt: 'auto',
          pt: { xs: 0.75, sm: 1 },
          ...(isCentered && { justifyContent: 'center' }),
        }}>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              fontWeight: 600,
              color: '#000',
            }}
          >
            {priceLabel}
          </Typography>
          {hasNumericPrices && product.originalPrice > product.price && (
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                color: '#999',
                textDecoration: 'line-through',
              }}
            >
              {product.originalPrice.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* Color Options - Clean Dots */}
        {product.colors && product.colors.length > 0 && (
          <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.4 }, mt: { xs: 0.75, sm: 1 }, ...(isCentered && { justifyContent: 'center' }) }}>
            {product.colors.slice(0, 5).map((color, index) => (
              <Box
                key={index}
                sx={{
                  width: { xs: 10, sm: 12, md: 14 },
                  height: { xs: 10, sm: 12, md: 14 },
                  borderRadius: '50%',
                  backgroundColor: color,
                  border: '1px solid rgba(0,0,0,0.1)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              />
            ))}
            {product.colors.length > 5 && (
              <Typography sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                color: '#999',
                ml: { xs: 0.25, sm: 0.5 },
                alignSelf: 'center'
              }}>
                +{product.colors.length - 5}
              </Typography>
            )}
          </Box>
        )}

        {/* Always-visible action for quickAdd templates (mobile friendly) */}
        {cardInfo === 'quickAdd' && (
          <Button
            fullWidth
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleProductClick();
            }}
            sx={{
              mt: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              fontWeight: 600,
              py: 0.6,
              borderRadius: 'var(--button-border-radius, 6px)',
              borderColor: 'var(--primary)',
              color: 'var(--primary)',
              transition: 'all var(--motion-speed, 0.3s) var(--motion-ease, ease)',
              '&:hover': {
                backgroundColor: 'var(--primary)',
                color: '#fff',
                borderColor: 'var(--primary)',
              },
            }}
          >
            View Details
          </Button>
        )}
      </Box>

      {/* Snackbar for favorite notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'var(--primary)',
            fontSize: '0.9rem',
            fontWeight: 500,
          }
        }}
      />

    </Box>
  );
};

export default ProductCard;
